import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Pencil, Trash2, LogOut, UserPlus, Search, RefreshCw, Users as UsersIcon, Building2 } from "lucide-react";
import { toast } from "sonner";
import { BulkBar, RowCheck } from "./BulkBar";

const EMPTY_FORM = {
  name: "", email: "", password: "", phone: "",
  role: "resident", building_id: "", unit_id: "", building_ids: [],
};

export function AdminUsersTab({ buildings = [] }) {
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [buildingId, setBuildingId] = useState("");

  const [editing, setEditing] = useState(null); // user object or "new"
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [toDelete, setToDelete] = useState(null);
  const [toForceLogout, setToForceLogout] = useState(null);

  // --- bulk selection ----------------------------------------------------
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDelete, setBulkDelete] = useState(false);
  const [bulkRole, setBulkRole] = useState(null); // "" or role string
  const [bulkBuilding, setBulkBuilding] = useState(null); // "" or building id
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (role) params.role = role;
      if (buildingId) params.building_id = buildingId;
      const [u, un] = await Promise.all([
        api.get("/platform/users", { params }),
        api.get("/units"),
      ]);
      setUsers(u.data);
      setUnits(un.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load users");
    } finally { setLoading(false); }
  }, [q, role, buildingId]);

  useEffect(() => { load(); }, [load]);

  const unitsForBuilding = useMemo(
    () => units.filter((u) => !form.building_id || u.building_id === form.building_id),
    [units, form.building_id]
  );

  function openNew() {
    setForm(EMPTY_FORM);
    setEditing("new");
  }
  function openEdit(u) {
    setForm({
      name: u.name || "",
      email: u.email || "",
      password: "",
      phone: u.phone || "",
      role: u.role || "resident",
      building_id: u.building_id || "",
      unit_id: u.unit_id || "",
      building_ids: u.building_ids || [],
    });
    setEditing(u);
  }

  async function save() {
    setSaving(true);
    try {
      if (editing === "new") {
        const body = { ...form };
        if (!body.building_id) delete body.building_id;
        if (!body.unit_id) delete body.unit_id;
        if (!body.phone) delete body.phone;
        if (body.role !== "guard") delete body.building_ids;
        await api.post("/platform/users", body);
        toast.success("User created");
      } else {
        const body = {};
        ["name", "email", "phone", "role"].forEach((k) => {
          if (form[k] !== editing[k]) body[k] = form[k];
        });
        if (form.password) body.password = form.password;
        if ((form.building_id || "") !== (editing.building_id || "")) body.building_id = form.building_id;
        if ((form.unit_id || "") !== (editing.unit_id || "")) body.unit_id = form.unit_id;
        if (form.role === "guard") body.building_ids = form.building_ids;
        await api.patch(`/platform/users/${editing.id}`, body);
        toast.success("User updated");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function doDelete() {
    try {
      await api.delete(`/platform/users/${toDelete.id}`);
      toast.success("User deleted");
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  }

  async function doForceLogout() {
    try {
      await api.post(`/platform/users/${toForceLogout.id}/force-logout`);
      toast.success("Session invalidated");
      setToForceLogout(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Force logout failed");
    }
  }

  async function toggleActive(u) {
    try {
      await api.patch(`/platform/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Deactivated" : "Activated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
  }

  // --- bulk helpers ------------------------------------------------------
  function toggleRow(id, on) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }
  function toggleAllOnPage(on) {
    setSelected(() => (on ? new Set(users.map((u) => u.id)) : new Set()));
  }
  function clearSelection() { setSelected(new Set()); }

  function summarizeResult(res, verb) {
    const n = res.updated ?? res.deleted ?? res.revoked ?? 0;
    const skippedN = (res.skipped?.length || res.failed?.length || 0);
    if (skippedN) {
      const sample = (res.skipped || res.failed || []).slice(0, 2)
        .map((s) => s.reason).join("; ");
      toast.success(`${n} ${verb} · ${skippedN} skipped (${sample}${skippedN > 2 ? "…" : ""})`);
    } else {
      toast.success(`${n} ${verb}`);
    }
  }

  async function runBulkDelete() {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/platform/bulk/users/delete", { ids: Array.from(selected) });
      summarizeResult(data, "deleted");
      setBulkDelete(false);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkBusy(false); }
  }

  async function runBulkRole() {
    if (!bulkRole) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/platform/bulk/users/patch", {
        ids: Array.from(selected), set: { role: bulkRole },
      });
      summarizeResult(data, "updated");
      setBulkRole(null);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk role change failed");
    } finally { setBulkBusy(false); }
  }

  async function runBulkBuilding() {
    if (bulkBuilding === null) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/platform/bulk/users/patch", {
        ids: Array.from(selected), set: { building_id: bulkBuilding || "" },
      });
      summarizeResult(data, "moved");
      setBulkBuilding(null);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk building move failed");
    } finally { setBulkBusy(false); }
  }

  const allOnPageChecked = users.length > 0 && users.every((u) => selected.has(u.id));
  const someOnPageChecked = users.some((u) => selected.has(u.id));

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      <div className="card-stroke p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <Label className="label-tiny">Search</Label>
          <div className="relative mt-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="name or email"
                   className="pl-9" data-testid="users-filter-q"/>
          </div>
        </div>
        <div className="w-40">
          <Label className="label-tiny">Role</Label>
          <Select value={role || "all"} onValueChange={(v)=>setRole(v==="all"?"":v)}>
            <SelectTrigger className="mt-2" data-testid="users-filter-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="resident">Resident</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="guard">Guard</SelectItem>
              <SelectItem value="super_admin">Super admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-56">
          <Label className="label-tiny">Building</Label>
          <Select value={buildingId || "all"} onValueChange={(v)=>setBuildingId(v==="all"?"":v)}>
            <SelectTrigger className="mt-2" data-testid="users-filter-building"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All buildings</SelectItem>
              {buildings.map((b)=>(<SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} data-testid="users-refresh">
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`}/> Refresh
        </Button>
        <Button onClick={openNew} data-testid="users-new">
          <UserPlus size={14} className="mr-1.5"/> New user
        </Button>
      </div>

      <div className="card-stroke p-0 overflow-x-auto">
        <BulkBar
          count={selected.size}
          onClear={clearSelection}
          testId="users-bulk-bar"
        >
          <Button size="sm" variant="outline" onClick={() => setBulkRole("")}
                  data-testid="users-bulk-role">
            <UsersIcon size={14} className="mr-1.5"/> Change role…
          </Button>
          <Button size="sm" variant="outline" onClick={() => setBulkBuilding("")}
                  data-testid="users-bulk-move">
            <Building2 size={14} className="mr-1.5"/> Move to building…
          </Button>
          <Button size="sm" variant="outline"
                  onClick={() => setBulkDelete(true)}
                  className="text-destructive hover:text-destructive"
                  data-testid="users-bulk-delete">
            <Trash2 size={14} className="mr-1.5"/> Delete
          </Button>
        </BulkBar>
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="label-tiny text-left border-b border-border">
              <th className="py-3 pl-4 pr-2 w-10">
                <RowCheck
                  checked={allOnPageChecked}
                  onChange={(on) => toggleAllOnPage(on || someOnPageChecked ? on : true)}
                  testId="users-select-all"
                  label="Select all on page"
                />
              </th>
              <th>Name</th><th>Email</th><th>Role</th>
              <th>Building</th><th>Unit</th><th>Status</th><th className="text-right pr-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={8} className="py-8 text-center text-muted-foreground" data-testid="users-empty">
                {loading ? "Loading…" : "No users found."}
              </td></tr>
            ) : users.map((u) => (
              <tr key={u.id} className="border-b border-border last:border-0" data-testid={`user-row-${u.id}`}>
                <td className="py-3 pl-4 pr-2">
                  <RowCheck
                    checked={selected.has(u.id)}
                    onChange={(on) => toggleRow(u.id, on)}
                    testId={`user-select-${u.id}`}
                  />
                </td>
                <td>{u.name}</td>
                <td className="font-mono text-xs">{u.email}</td>
                <td>
                  <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full ${
                    u.role === "super_admin" ? "bg-accent text-accent-foreground"
                    : u.role === "admin" ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-secondary-foreground"
                  }`}>{u.role}</span>
                </td>
                <td className="font-mono text-xs">{u.building_code || "—"}</td>
                <td className="font-mono text-xs">{u.unit_number || "—"}</td>
                <td>
                  <button
                    onClick={()=>toggleActive(u)}
                    className={`text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full ${
                      u.is_active === false ? "bg-destructive/15 text-destructive" : "bg-emerald-500/15 text-emerald-600"
                    }`}
                    data-testid={`user-toggle-${u.id}`}
                  >{u.is_active === false ? "inactive" : "active"}</button>
                </td>
                <td className="text-right pr-4 whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={()=>openEdit(u)} data-testid={`user-edit-${u.id}`} aria-label="Edit"><Pencil size={14}/></Button>
                  <Button size="icon" variant="ghost" onClick={()=>setToForceLogout(u)} data-testid={`user-force-logout-${u.id}`} aria-label="Force logout"><LogOut size={14}/></Button>
                  <Button size="icon" variant="ghost" onClick={()=>setToDelete(u)}
                          disabled={u.role === "super_admin"}
                          data-testid={`user-delete-${u.id}`} aria-label="Delete">
                    <Trash2 size={14} className="text-destructive"/>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o)=>{ if (!o) setEditing(null); }}>
        <DialogContent data-testid="user-modal">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Create user" : `Edit ${editing?.name || ""}`}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="label-tiny">Name</Label>
              <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-2" data-testid="user-form-name"/></div>
            <div className="col-span-2"><Label className="label-tiny">Email</Label>
              <Input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="mt-2 font-mono text-xs" data-testid="user-form-email"/></div>
            <div><Label className="label-tiny">Password {editing !== "new" && "(leave blank to keep)"}</Label>
              <Input type="password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} className="mt-2 font-mono" data-testid="user-form-password"/></div>
            <div><Label className="label-tiny">Phone</Label>
              <Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="mt-2" data-testid="user-form-phone"/></div>
            <div><Label className="label-tiny">Role</Label>
              <Select value={form.role} onValueChange={(v)=>setForm({...form, role: v, unit_id: v === "resident" ? form.unit_id : ""})}>
                <SelectTrigger className="mt-2" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="resident">Resident</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="guard">Guard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="label-tiny">Building</Label>
              <Select value={form.building_id || "none"} onValueChange={(v)=>setForm({...form, building_id: v === "none" ? "" : v, unit_id: ""})}>
                <SelectTrigger className="mt-2" data-testid="user-form-building"><SelectValue placeholder="None"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {buildings.map((b)=>(<SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {form.role === "resident" && (
              <div className="col-span-2"><Label className="label-tiny">Unit</Label>
                <Select value={form.unit_id || "none"} onValueChange={(v)=>setForm({...form, unit_id: v === "none" ? "" : v})}>
                  <SelectTrigger className="mt-2" data-testid="user-form-unit"><SelectValue placeholder="None"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {unitsForBuilding.map((u)=>(<SelectItem key={u.id} value={u.id}>{u.number}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditing(null)} data-testid="user-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.email || (editing === "new" && !form.password)}
                    data-testid="user-form-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o)=>{ if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the account and any unit assignments. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                               data-testid="user-delete-confirm">
              Delete user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toForceLogout} onOpenChange={(o)=>{ if (!o) setToForceLogout(null); }}>
        <AlertDialogContent data-testid="user-force-logout-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Force logout {toForceLogout?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Their current session tokens become invalid immediately — they'll need to sign in again on every device.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-force-logout-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doForceLogout} data-testid="user-force-logout-confirm">
              Force logout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDelete} onOpenChange={setBulkDelete}>
        <AlertDialogContent data-testid="users-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} user{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Self and the bootstrap super admin are skipped automatically. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="users-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runBulkDelete}
              disabled={bulkBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="users-bulk-delete-confirm"
            >
              {bulkBusy ? "Deleting…" : `Delete ${selected.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={bulkRole !== null} onOpenChange={(o)=>{ if (!o) setBulkRole(null); }}>
        <DialogContent data-testid="users-bulk-role-dialog">
          <DialogHeader>
            <DialogTitle>Change role for {selected.size} user{selected.size === 1 ? "" : "s"}</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="label-tiny">New role</Label>
            <Select value={bulkRole || ""} onValueChange={setBulkRole}>
              <SelectTrigger className="mt-2" data-testid="users-bulk-role-select"><SelectValue placeholder="Pick a role"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="resident">Resident</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="guard">Guard</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setBulkRole(null)} data-testid="users-bulk-role-cancel">Cancel</Button>
            <Button onClick={runBulkRole} disabled={!bulkRole || bulkBusy}
                    data-testid="users-bulk-role-apply">
              {bulkBusy ? "Updating…" : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkBuilding !== null} onOpenChange={(o)=>{ if (!o) setBulkBuilding(null); }}>
        <DialogContent data-testid="users-bulk-building-dialog">
          <DialogHeader>
            <DialogTitle>Move {selected.size} user{selected.size === 1 ? "" : "s"} to a building</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-muted-foreground mb-3">
            Moving a user to a different building clears any unit assignment.
          </div>
          <div>
            <Label className="label-tiny">Building</Label>
            <Select value={bulkBuilding || "none"} onValueChange={(v)=>setBulkBuilding(v === "none" ? "" : v)}>
              <SelectTrigger className="mt-2" data-testid="users-bulk-building-select"><SelectValue placeholder="Pick a building"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Unassign —</SelectItem>
                {buildings.map((b)=>(<SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setBulkBuilding(null)} data-testid="users-bulk-building-cancel">Cancel</Button>
            <Button onClick={runBulkBuilding} disabled={bulkBusy || bulkBuilding === null}
                    data-testid="users-bulk-building-apply">
              {bulkBusy ? "Moving…" : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
